import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import useSound from "use-sound";
import toast from "react-hot-toast";
import { Dialog } from "primereact/dialog";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import moment from "moment";

import Menu from "./Menu/Menu";
import StandingCard from "./StandingCard/StandingCard";
import TopCard from "./TopCard/TopCard";
import Filter from "./Filter/Filter";
import useSize from "../../../Utils/useSize/useSize";
import Backend from "../../../Backend/Backend";
import Loading from "../../../Utils/Loading/Loading";
import NotFound from "../../../Utils/NotFound/NotFound";
import ErrorSoundFile from "../../../Sound/Error.wav";
import BuySell from "../../../Screen/User/WatchlistUser/BuySell/BuySell";
import { useSelector } from "react-redux";

const backend = await new Backend().init();

export default function Standing({ user_id }) {
  const userType = Number(localStorage.getItem("user_type") || 7);

  const total = useSelector((state) => state.totalmodal);

  const dispatch = useDispatch();
  const windowSize = useSize();
  const [playError] = useSound(ErrorSoundFile);
  const filterRef = useRef();

  const [loading, setLoading] = useState(false);
  const [standingDelete, setStandingDelete] = useState(false);
  const [positions, setPositions] = useState([]);
  const [allPositions, setAllPositions] = useState([]);
  const [filteredPositions, setFilteredPositions] = useState([]);

  const [buysell, setBuySell] = useState(false);
  const [buyselltype, setBuySellType] = useState(0);

  /** ✅ Build common user data */
  const buildUserData = useCallback(
    () => ({
      server: localStorage.getItem("server"),
      id: localStorage.getItem("id"),
      username: localStorage.getItem("username"),
      token: localStorage.getItem("token"),
      super_id: user_id,
      master_id: 0,
      user_id: 0,
    }),
    [user_id]
  );

  /** ✅ Main loader */
  const loadStanding = useCallback(async () => {
    setLoading(true);
    try {
      let data = buildUserData();

      let res = await backend.standing_admin(data);

      if (res.error === "False") {
        setPositions(res.standing);
        setAllPositions(res.standing);
        setFilteredPositions(res.standing);
        setStandingDelete(res.standing_delete);
        dispatch({ type: "CLEAR_PFMODAL" });
      } else {
        if (res.logout === 1) {
          window.LogOut?.();
        } else {
          playError();
          toast.error(res.message || "Failed to load data");
        }
      }
    } catch (err) {
      console.error("Standing Load Error:", err);
      playError();
      toast.error("Failed to load standing data.");
    } finally {
      setLoading(false);
    }
  }, [buildUserData, dispatch, playError]);

  /** 🔹 Initial Load */
  useEffect(() => {
    loadStanding();
  }, [loadStanding]);

  /** 🔹 Symbol Search */
  const handleSearch = useCallback(
    (text) => {
      if (text.length > 1) {
        const filtered = allPositions.filter((x) =>
          x.symbol.toLowerCase().includes(text.toLowerCase())
        );
        setPositions(filtered);
        setFilteredPositions(filtered);
      } else {
        setPositions(allPositions);
        setFilteredPositions(allPositions);
      }
    },
    [allPositions]
  );

  /** 🔹 Manual Reload */
  const handleReload = useCallback(() => loadStanding(), [loadStanding]);

  /** 🔹 Close Modal + Reload */
  const closeModal = useCallback(() => {
    setBuySell(false);
    loadStanding();
  }, [loadStanding]);

  /** 🔹 Open Modal */
  const open = (a, b) => {
    window.symbolData = a;
    setBuySell(true);
    setBuySellType(b);
  };

  /** 📊 Get Exchange Name */
  const getExchangeName = useCallback((item) => {
    switch (item.type) {
      case "nse":
        return "NSE";
      case "mcx":
        return "MCX";
      case "fo":
        return "OPTIONS";
      case "nse_cash":
        return "NSE-EQUITY";
      case "comex_future":
        return "COMEX";
      case "crypto_future":
        return "CRYPTO";
      case "forex_future":
        return "FOREX";
      default:
        return "NSE";
    }
  }, []);

  /** 📊 Get Expiry Label */
  const getExpiryLabel = useCallback((item) => {
    if (item.type === "nse_cash") return "EQUITY";
    return moment(item.expiry_date).format("DD-MMM-YYYY");
  }, []);

  /** 📊 Get Buy/Sell Label */
  const getBuySellLabel = useCallback((item) => {
    return item.buy_sell === 0 ? "BUY" : "SELL";
  }, []);

  /** 📊 Professional PDF Export with Full Data */
  const exportToPDF = useCallback(() => {
    if (filteredPositions.length === 0) {
      toast.error("No data to export");
      return;
    }

    try {
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4',
        compress: true
      });

      const pageWidth = doc.internal.pageSize.getWidth();

      const colors = {
        primary: [44, 62, 80],
        success: [46, 204, 113],
        accent: [231, 76, 60],
        textLight: [127, 140, 141],
        border: [220, 220, 220],
      };

      const totalPL = total || 0;
      const totalRecords = filteredPositions.length;
      const totalQty = filteredPositions.reduce((sum, item) => sum + (item.qty || 0), 0);
      const totalAmount = filteredPositions.reduce((sum, item) => sum + (item.limit_block || 0), 0);

      // Header
      doc.setFillColor(...colors.primary);
      doc.rect(0, 0, pageWidth, 16, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Standing Report", pageWidth / 2, 10, { align: "center" });

      // Info line
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...colors.textLight);
      const generated = new Date().toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
      doc.text(`Generated: ${generated}   Records: ${totalRecords}   Qty: ${totalQty}`, 15, 24);

      doc.setFont("helvetica", "bold");
      doc.setTextColor(...(totalPL >= 0 ? colors.success : colors.accent));
      doc.text(`Total P&L: ${totalPL.toFixed(2)}`, pageWidth - 15, 24, { align: "right" });

      // Table
      const tableData = filteredPositions.map((item, index) => {
        const isBuy = item.buy_sell === 0;
        return [
          (index + 1).toString(),
          getExchangeName(item),
          item.username || "",
          item.symbol || "",
          getExpiryLabel(item),
          {
            content: getBuySellLabel(item),
            styles: {
              textColor: isBuy ? colors.success : colors.accent,
              fontStyle: 'bold',
            }
          },
          (item.qty || 0).toString(),
          (item.rate ?? "").toString(),
          (item.limit_block || 0).toString(),
          moment(item.date_created).format("DD-MM-YYYY"),
          moment(item.date_update).format("DD-MM-YYYY"),
        ];
      });

      doc.autoTable({
        startY: 30,
        head: [[
          '#', 'Exchange', 'User', 'Symbol', 'Expiry', 'Type',
          'Qty', 'Rate', 'Amount', 'Created', 'Updated'
        ]],
        body: tableData,
        theme: 'grid',
        styles: {
          fontSize: 7,
          cellPadding: 2,
          lineColor: colors.border,
          lineWidth: 0.1,
        },
        headStyles: {
          fillColor: colors.primary,
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          halign: 'center',
        },
        columnStyles: {
          0: { cellWidth: 8, halign: 'center' },
          5: { halign: 'center' },
          6: { halign: 'right' },
          7: { halign: 'right' },
          8: { halign: 'right', fontStyle: 'bold' },
          9: { halign: 'center' },
          10: { halign: 'center' },
        },
        alternateRowStyles: { fillColor: [248, 249, 250] },
        didDrawPage: (data) => {
          const footerY = doc.internal.pageSize.getHeight() - 8;
          doc.setDrawColor(...colors.border);
          doc.line(15, footerY - 3, pageWidth - 15, footerY - 3);
          doc.setFontSize(7);
          doc.setTextColor(...colors.textLight);
          doc.text(`Page ${data.pageNumber} of ${doc.internal.getNumberOfPages()}`, 15, footerY + 1);
          doc.text(
            `Total: ${totalRecords} records | Qty: ${totalQty} | Amount: ${totalAmount}`,
            pageWidth / 2, footerY + 1, { align: "center" }
          );
        },
        margin: { top: 5, bottom: 10, left: 10, right: 10 },
      });

      const fileName = `Standing_Report_${new Date().toISOString().split("T")[0]}.pdf`;
      doc.save(fileName);
      toast.success("PDF exported successfully!");
    } catch (error) {
      console.error("PDF Export Error:", error);
      toast.error("Failed to export PDF");
    }
  }, [filteredPositions, getExchangeName, getExpiryLabel, getBuySellLabel, total]);

  /** 📊 Professional Excel Export with Full Data */
  const exportToExcel = useCallback(() => {
    if (filteredPositions.length === 0) {
      toast.error("No data to export");
      return;
    }

    try {
      // Prepare full data for Excel
      const excelData = filteredPositions.map((item) => ({
        "#": filteredPositions.indexOf(item) + 1,
        "Exchange": getExchangeName(item),
        "Master": item.master_username || "",
        "User": item.username || "",
        "Symbol": item.symbol || "",
        "Expiry": getExpiryLabel(item),
        "Type": getBuySellLabel(item),
        "Qty": item.qty || 0,
        "Rate": (item.rate || 0).toFixed(2),
        "LTP": (item.ltp || 0).toFixed(2),
        "Net Qty": item.net_qty || 0,
        "Price": (item.price || 0).toFixed(2),
        "Amount": (item.amount || 0).toFixed(2),
        "MTM": (item.mtm || 0).toFixed(2),
        "Day P&L": (item.day_pl || 0).toFixed(2),
        "Total P&L": (item.total_pl || 0).toFixed(2),
        "Created": moment(item.date_created).format("DD-MM-YYYY HH:mm:ss"),
        "Updated": moment(item.date_update).format("DD-MM-YYYY HH:mm:ss"),
      }));

      // Calculate totals
      const totalPL = filteredPositions.reduce((sum, item) => sum + (item.total_pl || 0), 0);
      const totals = {
        "#": "TOTAL",
        "Exchange": "",
        "Master": "",
        "User": "",
        "Symbol": "",
        "Expiry": "",
        "Type": "",
        "Qty": filteredPositions.reduce((sum, item) => sum + (item.qty || 0), 0),
        "Rate": "",
        "LTP": "",
        "Net Qty": filteredPositions.reduce((sum, item) => sum + (item.net_qty || 0), 0),
        "Price": "",
        "Amount": filteredPositions.reduce((sum, item) => sum + (item.amount || 0), 0).toFixed(2),
        "MTM": filteredPositions.reduce((sum, item) => sum + (item.mtm || 0), 0).toFixed(2),
        "Day P&L": filteredPositions.reduce((sum, item) => sum + (item.day_pl || 0), 0).toFixed(2),
        "Total P&L": totalPL.toFixed(2),
        "Created": "",
        "Updated": "",
      };
      excelData.push(totals);

      // Create workbook
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      // Auto-size columns
      const colWidths = [
        { wch: 6 },  // #
        { wch: 14 }, // Exchange
        { wch: 18 }, // Master
        { wch: 18 }, // User
        { wch: 20 }, // Symbol
        { wch: 18 }, // Expiry
        { wch: 10 }, // Type
        { wch: 12 }, // Qty
        { wch: 14 }, // Rate
        { wch: 14 }, // LTP
        { wch: 12 }, // Net Qty
        { wch: 14 }, // Price
        { wch: 16 }, // Amount
        { wch: 16 }, // MTM
        { wch: 16 }, // Day P&L
        { wch: 18 }, // Total P&L
        { wch: 22 }, // Created
        { wch: 22 }, // Updated
      ];
      ws["!cols"] = colWidths;

      // Style header row
      const range = XLSX.utils.decode_range(ws['!ref']);
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
        if (!ws[cellAddress]) continue;
        ws[cellAddress].s = {
          fill: { fgColor: { rgb: "2980B9" } },
          font: { bold: true, color: { rgb: "FFFFFF" }, sz: 10 },
          alignment: { horizontal: "center", vertical: "center" }
        };
      }

      // Style total row
      const totalRow = excelData.length - 1;
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: totalRow, c: col });
        if (!ws[cellAddress]) continue;
        ws[cellAddress].s = {
          fill: { fgColor: { rgb: "F39C12" } },
          font: { bold: true, color: { rgb: "FFFFFF" }, sz: 10 },
          alignment: { horizontal: "center", vertical: "center" }
        };
      }

      // Add conditional formatting for P&L columns
      for (let r = 1; r < totalRow; r++) {
        // MTM (index 13)
        const mtmCell = XLSX.utils.encode_cell({ r: r, c: 13 });
        if (ws[mtmCell]) {
          const value = parseFloat(ws[mtmCell].v);
          ws[mtmCell].s = {
            font: { color: { rgb: value >= 0 ? "27AE60" : "E74C3C" }, bold: true },
            alignment: { horizontal: "right", vertical: "center" },
            numFmt: "0.00"
          };
        }

        // Day P&L (index 14)
        const dayPLCell = XLSX.utils.encode_cell({ r: r, c: 14 });
        if (ws[dayPLCell]) {
          const value = parseFloat(ws[dayPLCell].v);
          ws[dayPLCell].s = {
            font: { color: { rgb: value >= 0 ? "27AE60" : "E74C3C" }, bold: true },
            alignment: { horizontal: "right", vertical: "center" },
            numFmt: "0.00"
          };
        }

        // Total P&L (index 15)
        const totalPLCell = XLSX.utils.encode_cell({ r: r, c: 15 });
        if (ws[totalPLCell]) {
          const value = parseFloat(ws[totalPLCell].v);
          ws[totalPLCell].s = {
            font: { color: { rgb: value >= 0 ? "27AE60" : "E74C3C" }, bold: true },
            alignment: { horizontal: "right", vertical: "center" },
            numFmt: "0.00"
          };
        }

        // Type column (index 6) - color BUY/SELL
        const typeCell = XLSX.utils.encode_cell({ r: r, c: 6 });
        if (ws[typeCell]) {
          const value = ws[typeCell].v;
          ws[typeCell].s = {
            font: {
              color: { rgb: value === "BUY" ? "27AE60" : "E74C3C" },
              bold: true
            },
            alignment: { horizontal: "center", vertical: "center" }
          };
        }
      }

      // Add to workbook
      XLSX.utils.book_append_sheet(wb, ws, "Standing Report");

      // Generate Excel file
      const wbout = XLSX.write(wb, {
        bookType: "xlsx",
        type: "array",
        bookSST: false
      });

      const blob = new Blob([wbout], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      });

      const fileName = `Standing_Report_${new Date().toISOString().split("T")[0]}.xlsx`;
      saveAs(blob, fileName);

      toast.success("Excel exported successfully!");
    } catch (error) {
      console.error("Excel Export Error:", error);
      toast.error("Failed to export Excel");
    }
  }, [filteredPositions, getExchangeName, getExpiryLabel, getBuySellLabel]);

  /** 📊 CSV Export with Full Data */
  const exportToCSV = useCallback(() => {
    if (filteredPositions.length === 0) {
      toast.error("No data to export");
      return;
    }

    try {
      const headers = [
        "#",
        "Exchange",
        "Master",
        "User",
        "Symbol",
        "Expiry",
        "Type",
        "Qty",
        "Rate",
        "LTP",
        "Net Qty",
        "Price",
        "Amount",
        "MTM",
        "Day P&L",
        "Total P&L",
        "Created",
        "Updated"
      ];

      const rows = filteredPositions.map((item, index) => [
        index + 1,
        `"${getExchangeName(item)}"`,
        `"${item.master_username || ''}"`,
        `"${item.username || ''}"`,
        `"${item.symbol || ''}"`,
        `"${getExpiryLabel(item)}"`,
        `"${getBuySellLabel(item)}"`,
        item.qty || 0,
        (item.rate || 0).toFixed(2),
        (item.ltp || 0).toFixed(2),
        item.net_qty || 0,
        (item.price || 0).toFixed(2),
        (item.amount || 0).toFixed(2),
        (item.mtm || 0).toFixed(2),
        (item.day_pl || 0).toFixed(2),
        (item.total_pl || 0).toFixed(2),
        `"${moment(item.date_created).format("DD-MM-YYYY HH:mm:ss")}"`,
        `"${moment(item.date_update).format("DD-MM-YYYY HH:mm:ss")}"`,
      ]);

      const totalPL = filteredPositions.reduce((sum, item) => sum + (item.total_pl || 0), 0);
      const totalRow = [
        '"TOTAL"',
        "",
        "",
        "",
        "",
        "",
        "",
        filteredPositions.reduce((sum, item) => sum + (item.qty || 0), 0),
        "",
        "",
        filteredPositions.reduce((sum, item) => sum + (item.net_qty || 0), 0),
        "",
        filteredPositions.reduce((sum, item) => sum + (item.amount || 0), 0).toFixed(2),
        filteredPositions.reduce((sum, item) => sum + (item.mtm || 0), 0).toFixed(2),
        filteredPositions.reduce((sum, item) => sum + (item.day_pl || 0), 0).toFixed(2),
        totalPL.toFixed(2),
        "",
        "",
      ];

      const csvData = [
        `"Standing Report - ${new Date().toLocaleString()}"`,
        `"User: ${localStorage.getItem("username") || "N/A"}"`,
        `"Total Records: ${filteredPositions.length}"`,
        "",
        headers.join(","),
        ...rows.map(row => row.join(",")),
        "",
        totalRow.join(","),
        "",
        `"Generated: ${new Date().toLocaleString()}"`
      ];

      const csvContent = csvData.join("\n");
      const blob = new Blob(["\uFEFF" + csvContent], {
        type: "text/csv;charset=utf-8;"
      });

      const fileName = `Standing_Report_${new Date().toISOString().split("T")[0]}.csv`;
      saveAs(blob, fileName);

      toast.success("CSV exported successfully!");
    } catch (error) {
      console.error("CSV Export Error:", error);
      toast.error("Failed to export CSV");
    }
  }, [filteredPositions, getExchangeName, getExpiryLabel, getBuySellLabel]);

  /** ✅ Responsive Layout */
  return (
    <div
      style={{
        width: windowSize[0] - 100,
        height: windowSize[1] - 170,
      }}
    >
      <Filter
        ref={filterRef}
        search={handleSearch}
        records={filteredPositions.length}
        onExportPDF={exportToPDF}
        onExportExcel={exportToExcel}
        onExportCSV={exportToCSV}
      />

      <TopCard user_id={user_id} />
      <Menu standing_delete={standingDelete} userType={userType} />

      <div
        key={loading ? "loading" : "loaded"}
        style={{
          width: windowSize[0] - 100,
          height: windowSize[1] - 330,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {loading ? (
          <div className="loadingContainer">
            <Loading height={windowSize[1] - 330} />
          </div>
        ) : filteredPositions.length > 0 ? (
          <div
            style={{
              width: windowSize[0] - 100,
              height: windowSize[1] - 330,
              overflowY: "auto",
            }}
          >
            {filteredPositions.map((item, index) => (
              <StandingCard
                key={item.id}
                item={item}
                index={index + 1}
                total={filteredPositions.length}
                reload={handleReload}
                standing_delete={standingDelete}
                userType={userType}
                open={open}
              />
            ))}
          </div>
        ) : (
          <NotFound
            height={windowSize[1] - 330}
            title="Nothing here"
            subtext="No Standing Found."
          />
        )}
      </div>

      {/* ✅ Modal Section */}
      <Dialog
        visible={buysell}
        closeOnEscape
        closeIcon
        onHide={closeModal}
        className="modal"
        id="modal"
        position="center"
        transitionOptions={false}
        focusOnShow={false}
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: windowSize[0],
          height: windowSize[1] - 100,
        }}
      >
        <div
          style={{
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-end",
          }}
        >
          <BuySell
            buysell={buysell}
            symbolData={window.symbolData}
            close={closeModal}
            buyselltype={buyselltype}
          />
        </div>
      </Dialog>
    </div>
  );
}